import React, { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import classNames from "classnames";

import Icon from "../../Icon/Icon";

import styles from "../styles/Input.module.css";
import listBoxStyles from "./ListBox.module.css";

export interface ListboxItem {
    id: string | number;
    label: string | number;
    value: string | number;
}

interface SelectEvent {
    target: {
        value: ListboxItem;
        name: string | number;
    };
}

interface Props {
    name: string;
    label?: string;

    error?: boolean;
    disabled?: boolean;
    selectedItem?: ListboxItem;
    items: ListboxItem[];
    onSelect?: (selected: ListboxItem, event: SelectEvent) => void;
    styleOverride?: string;
}

const ListBox = React.forwardRef<HTMLDivElement, Props>(
    ({ name, label, selectedItem, items, error, disabled, onSelect, styleOverride }, ref) => {
        const listBoxButtonClassName = classNames(`${styles.input} flex items-center justify-between gap-0.5 ${styleOverride}`, {
            [styles.inputError]: error,
            [styles.inputDisabled]: disabled
        });

        const currentItem = selectedItem || {
            id: "select",
            label: "Please select",
            value: "select"
        };

        const handleChange = !disabled && onSelect ? (item: ListboxItem) => {
            onSelect(item, {
                target: {
                    value: item,
                    name
                }
            });
        } : undefined;
        
        return (
            <Listbox as="div" ref={ref} name={name} value={currentItem} onChange={handleChange} disabled={disabled} className="relative w-full">
                {label ? <Listbox.Label className={styles.inputLabel}>{label}</Listbox.Label> : null}
                <Listbox.Button className={listBoxButtonClassName}>
                    <span className="block truncate">{currentItem.label}</span>
                    <Icon icon="chevron_down" className="flex-grow-0 flex-shrink-0" />
                </Listbox.Button>
                <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <Listbox.Options className={listBoxStyles.Options}>
                        {items.map((item) => (
                            <Listbox.Option
                                key={item.id}
                                className={({ active }) =>
                                    `relative select-none py-0.5 pl-2 pr-1 cursor-pointer ${
                                        active ? " bg-mint-300 text-pine-900" : "text-gray-900"
                                    }`
                                }
                                value={item}
                                title={item.label.toString()}
                            >
                                {({ selected, active }) => (
                                    <Fragment>
                                        <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{item.label}</span>
                                        {selected ? (
                                            <span
                                                className={`absolute inset-y-0 left-0 flex items-center pl-0.5 ${
                                                    active ? "text-pine-900" : "text-mint-300"
                                                }`}
                                            >
                                                <Icon icon="success" />
                                            </span>
                                        ) : null}
                                    </Fragment>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </Listbox>
        );
    }
);

export default ListBox;
